import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Check, AlertTriangle } from 'lucide-react';
import { SECTIONS } from '@/constants/application';
import { useApplicationProgress } from '@/hooks/use-application-progress';
import type { Application } from '@/types/database.types';

// Extend the Application type to include the new PEO preference fields
interface ExtendedApplication extends Application {
  ideal_decision_date?: string | null;
  ideal_go_live_date?: string | null;
  preferred_api_integrations?: string[] | null;
  top_peo_selection_priorities?: string[] | null;
  additional_notes?: string | null;
}

interface ApplicationSectionProgressProps {
  data: Partial<ExtendedApplication>;
  onSectionChange: (section: number) => void;
}

// Helper function to count missing required fields for each section
const getMissingFieldsCount = (sectionName: string, data: Partial<ExtendedApplication>): number => {
  if (!data) return 0;
  
  let missingCount = 0;
  
  switch (sectionName) {
    case 'Contact Information':
      if (!data.first_name) missingCount++;
      if (!data.last_name) missingCount++;
      if (!data.email) missingCount++;
      if (!data.phone) missingCount++;
      break;
      
    case 'Organization Information':
      // Basic company info
      if (!(data.company_legal_name || data.company_dba)) missingCount++;
      if (!data.naics_code) missingCount++;
      if (!data.corporate_structure) missingCount++;
      
      // Employee counts - require at least one meaningful count
      const hasEmployeeCounts = !!(
        data.w2_employee_count || 
        data.exempt_employee_count || 
        data.non_exempt_employee_count
      );
      if (!hasEmployeeCounts) missingCount++;
      
      // States with employees
      if (!Array.isArray(data.states_with_employees) || data.states_with_employees.length === 0) {
        missingCount++;
      }
      break;
      
    case 'Benefits Details':
      // Current carriers - count only if completely empty
      if (!data.current_medical_carrier?.trim()) missingCount++;
      if (!data.current_workers_comp_carrier?.trim()) missingCount++;
      if (!data.current_ancillary_carrier?.trim()) missingCount++;
      
      // Desired carriers - count only if empty arrays
      if (!Array.isArray(data.desired_medical_carriers) || 
          data.desired_medical_carriers.length === 0 || 
          !data.desired_medical_carriers.some(c => c?.trim())) {
        missingCount++;
      }
      
      if (!Array.isArray(data.desired_ancillary_carriers) || 
          data.desired_ancillary_carriers.length === 0 || 
          !data.desired_ancillary_carriers.some(c => c?.trim())) {
        missingCount++;
      }
      break;
      
    case 'Operations & Risk':
      // Workers' Comp Information - 4 fields
      if (!data.current_workers_comp_carrier?.trim()) missingCount++;
      if (!data.workers_comp_renewal_date) missingCount++;
      if (!data.company_services_description?.trim()) missingCount++;
      if (!data.physical_operations_description?.trim()) missingCount++;
      break;
      
    case 'Employee Health Assessment':
      // Only count if NO medical questions have been answered at all
      const medicalFields = [
        'has_kidney_disease', 'has_diabetes', 'has_liver_disease', 'has_neural_disorder',
        'has_cardiovascular', 'has_respiratory_disease', 'has_musculoskeletal', 'has_transplants',
        'has_cancer', 'has_hiv_aids', 'has_mental_health', 'has_nervous_system',
        'has_other_conditions', 'has_premature_birth'
      ];
      const hasAnyMedicalResponse = medicalFields.some(field => 
        data[field as keyof ExtendedApplication] === true || data[field as keyof ExtendedApplication] === false
      );
      if (!hasAnyMedicalResponse) missingCount = 1; // Count as one field to complete
      break;
      
    case 'Payroll Schedule':
      // Pay frequency
      if (!data.pay_schedule_type) missingCount++;
      // Current period start
      if (!data.pay_period_start) missingCount++;
      // Current period end
      if (!data.pay_period_end) missingCount++;
      // Next pay date for this schedule
      if (!data.next_check_date) missingCount++;
      // Full time count
      if (!data.primary_full_time_count) missingCount++;
      // Part time count
      if (!data.primary_part_time_count) missingCount++;
      break;
      
    case 'Document Upload':
      // Count each document type that hasn't been uploaded
      if (!data.employee_census_file) missingCount++;
      if (!data.dependent_census_file) missingCount++;
      if (!data.workers_comp_declaration_file) missingCount++;
      if (!data.workers_comp_loss_runs_file) missingCount++;
      if (!data.health_insurance_renewal_file) missingCount++;
      if (!data.tax_exempt_status_file) missingCount++;
      break;
      
    case 'PEO Preferences':
      // Count the guidance question if not answered
      if (data.needs_guidance !== true && data.needs_guidance !== false) missingCount++;
      
      // Count preference arrays - all should be filled out
      if (!Array.isArray(data.top_3_peo_needs) || data.top_3_peo_needs.length === 0) missingCount++;
      if (!Array.isArray(data.preferred_peos) || data.preferred_peos.length === 0) missingCount++;
      if (!Array.isArray(data.excluded_peos) || data.excluded_peos.length === 0) missingCount++;
      
      // Count the new date fields
      if (!data.ideal_decision_date) missingCount++;
      if (!data.ideal_go_live_date) missingCount++;
      break;
      
    default:
      break;
  }
  
  return missingCount;
};

export const ApplicationSectionProgress = ({ data, onSectionChange }: ApplicationSectionProgressProps) => {
  const { getCompletedSections } = useApplicationProgress(data as Application);
  const completedSections = getCompletedSections();

  const handleSectionClick = (sectionIndex: number) => {
    // Don't allow navigation to Review & Submit if other sections aren't complete
    if (sectionIndex === SECTIONS.length - 1) {
      const allSectionsExceptLast = Array.from({ length: SECTIONS.length - 1 }, (_, i) => i);
      const incompleteCount = allSectionsExceptLast.filter(i => !completedSections.includes(i)).length;
      if (incompleteCount > 0) {
        return; // Prevent navigation to review page if sections incomplete
      }
    }
    onSectionChange(sectionIndex);
  };

  // Filter out the Review & Submit section (last section) since user is already on it
  const sectionsToShow = SECTIONS.slice(0, -1);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Application Progress</h3>
      <div className="grid gap-3">
        {sectionsToShow.map((sectionName, index) => {
          const isCompleted = completedSections.includes(index);
          const missingFieldsCount = isCompleted ? 0 : getMissingFieldsCount(sectionName, data);

          return (
            <Card
              key={index}
              className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                isCompleted 
                  ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                  : 'bg-orange-50 border-orange-200 hover:bg-orange-100'
              }`}
              onClick={() => handleSectionClick(index)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                    isCompleted 
                      ? 'bg-green-600 text-white' 
                      : 'bg-orange-500 text-white'
                  }`}>
                    {isCompleted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{sectionName}</h4>
                    <p className="text-xs text-muted-foreground">
                      {isCompleted ? 'Complete' : `We recommend reviewing ${missingFieldsCount} field${missingFieldsCount !== 1 ? 's' : ''}`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
